import { useRouter } from 'next/router'

import classnames from 'classnames'

import type { AccordionItemProps } from '~/components/atoms/AccordionItem/AccordionItem'
import type { PortableTextComponentProps } from '~/components/atoms/BlockContent/BlockContent'
import BlockContent from '~/components/atoms/BlockContent/BlockContent'
import Button, { ButtonVariants } from '~/components/atoms/Button/Button'
import CollapsibleReadMore from '~/components/atoms/CollapsibleReadMore/CollapsibleReadMore'
import Container, { gutterTypes } from '~/components/atoms/Container/Container'
import Fixture from '~/components/atoms/Fixture/Fixture'
import { Col, Grid, Row } from '~/components/atoms/Grid'
import Spacer, { SpacerSizes } from '~/components/atoms/Spacer/Spacer'
import Text, { TextSizes } from '~/components/atoms/Text/Text'
import { ComponentTheme } from '~/components/themes'

import Accordion from '../Accordion/Accordion'

import styles from './TextWithAccordion.module.css'

export interface TextWithAccordionProps {
  title: string
  summary: string | PortableTextComponentProps[]
  beforeAccordionTextTitle?: string
  beforeAccordionText?: string | PortableTextComponentProps[]
  embeds?: PortableTextComponentProps[]
  data: AccordionItemProps[]
  sectionId?: string
  testID?: string
  links?: {
    url: string
    linkText: string
  }[]
}

export default function TextWithAccordion({
  title,
  summary,
  beforeAccordionTextTitle,
  beforeAccordionText,
  embeds,
  data,
  sectionId,
  testID,
  links,
}: TextWithAccordionProps) {
  const router = useRouter()

  const renderLinks = () => {
    return (
      <>
        <Spacer size={SpacerSizes.md} />
        {links &&
          links.map((link, idx) => (
            <div className={styles.link} key={`${link.url}-${idx}`}>
              <Spacer size={SpacerSizes.sm} />
              <Button
                link={link?.url}
                variant={ButtonVariants.secondaryDark}
                inline={true}
                iconLeft={true}
              >
                {link?.linkText}
              </Button>
            </div>
          ))}
      </>
    )
  }

  return (
    <div className={styles.base} data-testid={testID}>
      <Fixture verticalPadding={true} theme={ComponentTheme.primary}>
        <Container gutters={gutterTypes.always}>
          <Text
            Tag="h2"
            size={TextSizes.primaryTitleLrg}
            className={styles.title}
            id={sectionId}
          >
            {title}
          </Text>
          <Grid>
            <Row>
              <Col variant="col-1/2--lg">
                <div className={classnames(styles.firstText, 'prose')}>
                  <CollapsibleReadMore maxHeight={260}>
                    {typeof summary === 'string' ? (
                      <Text size={TextSizes.secondaryTitleMedReg}>
                        {summary}
                      </Text>
                    ) : (
                      <BlockContent
                        value={summary as any}
                        size={TextSizes.secondaryTitleMedReg}
                      ></BlockContent>
                    )}
                  </CollapsibleReadMore>
                  {embeds && (
                    <BlockContent value={embeds as any}></BlockContent>
                  )}
                </div>
              </Col>
              <Col variant="col-1/2--lg">
                {beforeAccordionText && (
                  <div className="prose">
                    <CollapsibleReadMore maxHeight={260} onlyMobile>
                      {beforeAccordionTextTitle && (
                        <Text
                          size={TextSizes.primaryTitleSml}
                          className={styles.beforeAccordionTextTitle}
                          Tag="h3"
                        >
                          {beforeAccordionTextTitle}
                        </Text>
                      )}
                      <BlockContent
                        value={beforeAccordionText as any}
                        size={TextSizes.bodyMed}
                      ></BlockContent>
                    </CollapsibleReadMore>
                  </div>
                )}
                <div className={styles.accordionWrap}>
                  <Accordion
                    data={data}
                    noContainer={true}
                    key={`title${router.asPath}`}
                  />
                </div>
                <div className={styles.linksSm}>{renderLinks()}</div>
              </Col>
            </Row>
          </Grid>
        </Container>
      </Fixture>
    </div>
  )
}
